import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  active: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10",
  paused: "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10",
  pending: "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10",
  rejected: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10",
};

export default function PromoteProduct({ products }) {
  const [ads, setAds] = useState([]);
  const [productId, setProductId] = useState("");
  const [copyText, setCopyText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ads/manage");
      setAds(data);
    } catch { /* not subscribed yet or none */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!productId || !copyText) { toast.error("Pick a product and write the ad copy"); return; }
    setSaving(true);
    try {
      await api.post("/ads/submit", { product_id: productId, copy_text: copyText });
      setCopyText("");
      toast.success("Advert submitted — pending admin approval");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit advert");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="promote-product-panel">
      <div className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-black text-xl tracking-tight">Promote a product</h2>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Submit an advert — once approved, VISMARKS pushes it across marketing channels and partner sites.
      </p>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="promote-product-select"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#FF3B30]">
          <option value="">Select a product…</option>
          {(products || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <input value={copyText} onChange={(e) => setCopyText(e.target.value)} placeholder="Ad copy buyers will see"
          maxLength={600} data-testid="promote-copy-input"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#FF3B30]" />
        <button onClick={submit} disabled={saving} data-testid="promote-submit-btn"
          className="h-10 rounded-full bg-[#FF3B30] text-white font-bold text-sm hover:bg-[#e0342a] transition-colors disabled:opacity-50">
          {saving ? "Submitting…" : "Submit advert"}
        </button>
      </div>

      {ads.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="promote-ads-list">
          {ads.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#27272A] bg-[#0A0A0A] px-4 py-2.5 text-sm">
              <span className="text-white font-medium truncate">{a.title}</span>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-neutral-500">{a.impressions} impr · {a.clicks} clicks</span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status]}`}>{a.status}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
